import { __rest } from "tslib";
import cn from 'classnames';
import * as React from 'react';
import { useContext, useEffect, useRef } from 'react';
import MediaQuery from 'react-responsive';
import { Field, FormSpy } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import LocationAutocomplete from '../../../../../../LocationAutocomplete';
import { LocationType, WebskyEventType } from '../../../../../../types';
import { emitEvent, MOBILE_MIN_WIDTH, useIsMobile, disableAutocompleteProps } from '../../../../../../utils';
import Value from './Value/Value';
import Tooltip from '../../../../../../BaseComponents/Tooltip/Tooltip';
import { ConfigContext } from '../../../../../../context';
import { useTheme } from '../../../../../../theme';
import { useRenderers } from '../../../../../../renderProps';
import { CdnIcon } from '@shared/ui';
export const IGNORE_CLICKOUTSIDE_CLASS = 'ignore_clickoutside';
let lastVisibleError = '';
export default React.memo(({ type, className = '', setLocation, locations, onSwap, recentSearches, segmentId, withMultiCity, renderInput }) => {
    const { Segment, Location: css } = useTheme('SearchForm');
    const { t } = useTranslation('SearchForm');
    const alternative = useContext(ConfigContext).SearchForm.mobileFormType !== 'common';
    const location = locations[type];
    const { SearchFormSegmentValue: SearchFormSegmentValueOverride = Value } = useRenderers();
    const additionalProps = {
        outsideClickIgnoreClass: IGNORE_CLICKOUTSIDE_CLASS
    };
    const isMobile = useIsMobile();
    const autocompleteRef = useRef();
    const inputRef = useRef();
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (e.target instanceof Node &&
                inputRef.current &&
                autocompleteRef.current &&
                !inputRef.current.contains(e.target) &&
                !autocompleteRef.current.contains(e.target)) {
                inputRef.current.blur();
            }
        };
        document.addEventListener('pointerdown', handleClickOutside);
        return () => {
            document.removeEventListener('pointerdown', handleClickOutside);
        };
    }, []);
    const validate = (value, allValues, meta) => {
        const data = meta.data;
        if (!data || !data[type]) {
            return t('Please select {{type}} airport', {
                type: t(type)
            });
        }
        if (data && data.arrival && data.departure && data.arrival.iata === data.departure.iata) {
            return t('Please select different airports');
        }
    };
    const onChange = (value) => {
        setLocation(value, type);
    };
    const onNearestAirportHandler = (locations) => {
        if (type === LocationType.Departure && segmentId === 0) {
            emitEvent(autocompleteRef === null || autocompleteRef === void 0 ? void 0 : autocompleteRef.current, {
                type: WebskyEventType.NearestAirportsLoaded,
                data: locations.map(location => ({
                    iata: location.iata,
                    name: location.name
                }))
            });
        }
    };
    const getRenderInput = (fieldState) => (isOpen, isLoading, inputProps) => {
        const { onChange, onFocus, onBlur } = inputProps, restProps = __rest(inputProps, ["onChange", "onFocus", "onBlur"]);
        const finalProps = restProps;
        finalProps.name = fieldState.input.name;
        if (inputProps.onBlur) {
            finalProps.onBlur = (event) => {
                onBlur(event);
                fieldState.input.onBlur(event);
            };
        }
        if (inputProps.onFocus) {
            finalProps.onFocus = (event) => {
                onFocus(event);
                fieldState.input.onFocus(event);
            };
        }
        if (inputProps.onChange) {
            finalProps.onChange = (event) => {
                onChange(event);
                fieldState.input.onChange(event);
            };
        }
        if (renderInput) {
            return renderInput({ inputProps: finalProps, isOpen, isLoading });
        }
        const autocompleteFocus = (event) => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
            inputProps.onFocus(event);
        };
        return (React.createElement(React.Fragment, null,
            React.createElement(MediaQuery, { minWidth: +MOBILE_MIN_WIDTH + 1 },
                React.createElement("input", Object.assign({}, finalProps, { className: cn(IGNORE_CLICKOUTSIDE_CLASS, Segment.input, className, css.input, {
                        [css.input_withoutPlaceholder]: !!location,
                        [css.input_departure]: type === LocationType.Departure,
                        [css.input_arrival]: type === LocationType.Arrival
                    }), ref: inputRef, placeholder: t(type === LocationType.Departure ? 'From' : 'To') }, disableAutocompleteProps))),
            React.createElement(MediaQuery, { maxWidth: MOBILE_MIN_WIDTH },
                React.createElement("div", { onClick: inputProps.onFocus, className: cn(Segment.input, className, css.input, {
                        [css.input_withoutPlaceholder]: !!location,
                        [css.input_departure]: type === LocationType.Departure,
                        [css.input_arrival]: type === LocationType.Arrival
                    }) },
                    !location && (React.createElement("div", { className: css.input_icon },
                        React.createElement(CdnIcon, { icon: "Info/Locations" }))),
                    React.createElement("span", { className: Segment.input_text }, t(type === LocationType.Departure ? 'From' : 'To')))),
            onSwap && (!isOpen || !isLoading) && !(isMobile && withMultiCity) && (React.createElement("button", { type: "button", className: cn(css.switcher, {
                    [css.switcher_highlighted]: isOpen,
                    [css.switcher_alternative]: alternative
                }), tabIndex: -1, onClick: onSwap },
                React.createElement(MediaQuery, { minWidth: +MOBILE_MIN_WIDTH + 1 },
                    React.createElement(CdnIcon, { icon: "Arrow/Swap" })),
                React.createElement(MediaQuery, { maxWidth: MOBILE_MIN_WIDTH }, alternative ? React.createElement(CdnIcon, { icon: "Arrow/Swap" }) : React.createElement(CdnIcon, { icon: "Arrow/Swap Vert" })))),
            React.createElement(SearchFormSegmentValueOverride, { className: Segment.location__value, type: type, isOpen: isOpen, isLoading: isLoading, location: location, inputValue: inputProps.value, withMultiCity: withMultiCity, onClick: autocompleteFocus })));
    };
    return (React.createElement(FormSpy, { subscription: {
            submitFailed: true
        } }, formState => (React.createElement(Field, { name: `${type}_${segmentId}`, validate: validate, subscription: {
            data: true,
            error: true
        } }, fieldState => {
        if (location !== null || typeof fieldState.meta.data[type] !== 'undefined') {
            formState.form.mutators.setFieldData(fieldState.input.name, locations);
        }
        if (fieldState.meta.error) {
            lastVisibleError = fieldState.meta.error;
        }
        return (React.createElement(Tooltip, { title: lastVisibleError, open: !!fieldState.meta.error && formState.submitFailed },
            React.createElement(LocationAutocomplete, { type: type, locations: locations, className: cn(css.autocomplete, {
                    [css.autocomplete_alternative]: alternative
                }), onSelect: onChange, recentSearches: recentSearches, inputRenderer: getRenderInput(fieldState), onClickOutsideProps: additionalProps, ref: autocompleteRef, onNearestAirports: onNearestAirportHandler })));
    }))));
});
